import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fmtAmount, equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";

const usd = (n, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

export default function PublicProposal() {
  const { token } = useParams();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sales/public/proposals/${token}`);
      setProp(data); setName(data.client?.full_name || "");
      if (data.status === "accepted") setAccepted(true);
    } catch (e) { setError("This estimate link is invalid or has expired."); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!agree) return toast.error("Please confirm your agreement to the estimated charges.");
    setBusy(true);
    try {
      await api.post(`/sales/public/proposals/${token}/accept`, { agree: true, acceptance_method: "email_link", accepted_by: name });
      setAccepted(true); toast.success("Estimate accepted. Thank you!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept"); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"><div className="text-center"><Icons.AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" /><p className="text-slate-600">{error}</p></div></div>;

  const cc = prop.currency_context || { usd_thb_rate: 33, show_thb: false };
  const eqTotal = (v) => equivalentFor(v, prop.currency || "USD", { rate: cc.usd_thb_rate, showThb: cc.show_thb });

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-proposal-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><Icons.Receipt className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-xl font-semibold text-slate-800">Expat Tax Firm</span>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 text-white px-6 py-5">
            <div className="text-sm text-slate-300">Estimate {prop.proposal_number}</div>
            <h1 className="font-heading text-2xl font-semibold">{prop.title}</h1>
            <div className="text-sm text-slate-300 mt-1">Prepared for {prop.client?.full_name}</div>
          </div>
          <div className="p-6 space-y-5">
            {prop.scope_summary && <div><h3 className="text-xs uppercase tracking-wide text-slate-400 mb-1">Scope</h3><p className="text-sm text-slate-700">{prop.scope_summary}</p></div>}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Estimated Charges</h3>
              <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
                {prop.line_items.map((li, i) => {
                  const eq = equivalentFor(li.amount, prop.currency || "USD", { rate: cc.usd_thb_rate, showThb: cc.show_thb });
                  return (
                  <div key={i} className="flex justify-between px-3 py-2.5 text-sm">
                    <span className="text-slate-700">{li.name} <span className="text-slate-400">×{li.quantity}</span></span>
                    <span className="inline-flex flex-col items-end leading-tight"><span className="font-medium">{usd(li.amount, prop.currency)}</span>{eq && <span className="text-[11px] text-slate-400">≈ {eq}</span>}</span>
                  </div>);})}
              </div>
              <div className="flex flex-col items-end gap-1 mt-3 text-sm">
                <div className="text-slate-500">Subtotal <span className="font-medium text-slate-800 ml-3">{usd(prop.subtotal, prop.currency)}</span>{eqTotal(prop.subtotal) && <span className="text-[11px] text-slate-400 ml-1">≈ {eqTotal(prop.subtotal)}</span>}</div>
                {prop.discount > 0 && <div className="text-slate-500">Discount <span className="font-medium text-rose-600 ml-3">-{usd(prop.discount, prop.currency)}</span></div>}
                <div className="text-lg font-semibold text-slate-900">Total <span className="ml-3">{usd(prop.total, prop.currency)}</span>{eqTotal(prop.total) && <span className="text-[11px] text-slate-400 ml-2 font-normal">≈ {eqTotal(prop.total)}</span>}</div>
              </div>
              {eqTotal(prop.total) && <FxDisclaimer className="mt-2 text-right" />}
            </div>
            {prop.assumptions && <div><h3 className="text-xs uppercase tracking-wide text-slate-400 mb-1">Assumptions</h3><p className="text-sm text-slate-600">{prop.assumptions}</p></div>}
            {prop.payment_terms && <p className="text-xs text-slate-400 italic">{prop.payment_terms}</p>}

            {accepted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="public-accepted">
                <Icons.CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
                <div className="font-medium text-emerald-800">Estimate Accepted</div>
                <p className="text-sm text-emerald-700">Thank you! Our team will follow up with your engagement letter.</p>
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-5">
                <div className="mb-3"><label className="text-xs text-slate-500">Your Name</label><input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-accept-name" className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mb-3">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="public-accept-agree" />
                  <span className="font-medium">I AGREE TO THE ESTIMATED CHARGES.</span>
                </label>
                <button onClick={accept} disabled={busy} data-testid="public-accept-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg py-3 font-medium flex items-center justify-center gap-2">
                  {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle className="h-4 w-4" />}Accept Estimate
                </button>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">Secured by Tax Flow · Expat Tax Firm</p>
      </div>
    </div>
  );
}
